import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext({ toast: { success: () => {}, error: () => {}, info: () => {} } });

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 300);
  }, []);

  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastId;
    setToasts(prev => [...prev.slice(-2), { id, message, type, exiting: false }]); // max 3
    setTimeout(() => removeToast(id), 3000);
  }, [removeToast]);

  const toast = {
    success: (msg) => addToast(msg, 'success'),
    error: (msg) => addToast(msg, 'error'),
    info: (msg) => addToast(msg, 'info'),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);

const ICONS = {
  success: <CheckCircle size={16} className="text-emerald-400 shrink-0" />,
  error: <AlertCircle size={16} className="text-red-400 shrink-0" />,
  info: <Info size={16} className="text-blue-400 shrink-0" />,
};

const BORDERS = {
  success: 'border-emerald-500/40',
  error: 'border-red-500/40',
  info: 'border-blue-500/40',
};

const ToastContainer = ({ toasts, onRemove }) => (
  <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-sm:left-4 max-sm:right-4">
    {toasts.map(t => (
      <ToastItem key={t.id} toast={t} onRemove={onRemove} />
    ))}
  </div>
);

const ToastItem = ({ toast: t, onRemove }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-xl border bg-surface backdrop-blur-xl shadow-lg text-sm text-txt-primary transition-all duration-300
        ${BORDERS[t.type]}
        ${visible && !t.exiting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    >
      {ICONS[t.type]}
      <span className="flex-1 min-w-0 truncate">{t.message}</span>
      <button onClick={() => onRemove(t.id)} className="text-txt-muted hover:text-txt-primary shrink-0">
        <X size={14} />
      </button>
    </div>
  );
};
